import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

const Toast = ({
  id,
  type = 'info',
  title,
  message,
  duration = 5000,
  onClose,
  ...props
}) => {
  const icons = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info
  };

  const styles = {
    success: {
      bg: 'bg-sage-green',
      border: 'border-sage-green/20',
      text: 'text-white',
      icon: 'text-white'
    },
    error: {
      bg: 'bg-red-500',
      border: 'border-red-500/20',
      text: 'text-white',
      icon: 'text-white'
    },
    warning: {
      bg: 'bg-orange-500',
      border: 'border-orange-500/20',
      text: 'text-white',
      icon: 'text-white'
    },
    info: {
      bg: 'bg-pragyavan-gold',
      border: 'border-pragyavan-gold/20',
      text: 'text-pragyavan-brown',
      icon: 'text-pragyavan-brown'
    }
  };

  const IconComponent = icons[type];
  const style = styles[type];

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  return (
    <AnimatePresence>
      <motion.div
        key={id}
        initial={{ opacity: 0, x: 300, scale: 0.9 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 300, scale: 0.9 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`
          relative w-80 max-w-sm rounded-xl shadow-lg border-2
          ${style.bg} ${style.border} ${style.text}
          p-4 pointer-events-auto
        `}
        {...props}
      >
        <div className="flex items-start space-x-3">
          <div className={`flex-shrink-0 ${style.icon}`}>
            <IconComponent className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            {title && (
              <p className="text-sm font-semibold mb-1">
                {title}
              </p>
            )}
            {message && (
              <p className="text-sm opacity-90">
                {message}
              </p>
            )}
          </div>
          
          <button
            onClick={onClose}
            className={`
              flex-shrink-0 ml-2 p-1 rounded-full
              ${style.icon} opacity-70 hover:opacity-100
              transition-opacity duration-200
            `}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress bar */}
        {duration > 0 && (
          <motion.div
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: duration / 1000, ease: 'linear' }}
            className="absolute bottom-0 left-0 h-1 bg-white/30 rounded-b-xl"
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default Toast;